/**
 * Editar a oportunidade, a partir do card do funil.
 *
 * ── Por que um diálogo próprio, e não o de criação com um `modo` ──────────
 *
 * Os dois formulários não têm os mesmos campos, e a diferença não é enfeite:
 * criar escolhe a ETAPA e editar não mexe nela — o contrato recusa mover e
 * editar no mesmo pedido, porque são duas escritas na engine e a segunda pode
 * falhar depois de a primeira valer. Em troca, editar oferece a PREVISÃO de
 * fechamento, que a criação não pede.
 *
 * Um componente com `modo: 'criar' | 'editar'` teria dois campos condicionais,
 * dois textos de botão e duas regras de "pode salvar" — e cada ajuste futuro
 * exigiria pensar nos dois casos. O que vale reaproveitar é reaproveitado: a
 * leitura do valor em micros e a busca de cliente vêm do arquivo da criação.
 *
 * ── Manda só o que MUDOU ──────────────────────────────────────────────────
 *
 * O contrato lê campo ausente como "não mexer". Enviar o formulário inteiro
 * funcionaria e seria pior: quem abriu o diálogo há dois minutos sobrescreveria
 * o valor que outra pessoa acabou de corrigir, sem ter tocado naquele campo.
 * O `version` recusaria o pedido nesse caso — mas só porque a edição alheia
 * mudou a versão; se ela tivesse mexido em OUTRO campo depois da nossa leitura,
 * o efeito seria apagar o trabalho dela.
 *
 * ── A data é `date`, e o instante nasce ao MEIO-DIA local ─────────────────
 *
 * Previsão de fechamento é um dia, não um horário. `type="date"` devolve
 * `2026-10-31`, e `new Date('2026-10-31')` lê isso como meia-noite UTC — que
 * em São Paulo é o dia 30 às 21h. Montando o instante ao meio-dia local, a data
 * sobrevive à ida e à volta em qualquer fuso do país.
 */

import { useState, type ReactNode } from 'react';
import type { CardDoFunil, MudancaDeCampos } from '@cliqcrm/contracts';
import { useCamposPersonalizados } from '@/shared/api/consultas';
import { CamposEditaveis } from '@/entities/campos/campo-personalizado';
import { Botao, Campo, Dialogo } from '@/shared/ui';
import { NotasParaCorrigir } from '@/features/comercial/notas-da-oportunidade';
import { EscolherCliente, emMicros, type EscolhaDeCliente } from './nova-oportunidade';

export interface EdicaoDaOportunidade {
  readonly titulo?: string;
  readonly valor?: { readonly quantia_micros: number; readonly moeda: string } | null;
  readonly fechamento_previsto_em?: string | null;
  readonly cliente_id?: string | null;
  /** Só os campos personalizados que mudaram (#568). */
  readonly campos?: MudancaDeCampos;
}

/** Instante → `YYYY-MM-DD` na data LOCAL, que é o que o campo mostra. */
function emDiaLocal(instante: string | null): string {
  if (!instante) return '';
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return '';
  const mes = `${data.getMonth() + 1}`.padStart(2, '0');
  const dia = `${data.getDate()}`.padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

/** `YYYY-MM-DD` → instante ao meio-dia local. Ver o cabeçalho. */
function emInstanteDoDia(dia: string): string | null {
  if (dia === '') return null;
  const [ano, mes, data] = dia.split('-').map(Number);
  if (!ano || !mes || !data) return null;
  return new Date(ano, mes - 1, data, 12, 0, 0).toISOString();
}

/** Em reais, como o campo mostra: o valor sai em micros do contrato. */
function emReais(valor: CardDoFunil['valor']): string {
  if (!valor) return '';
  return (valor.quantia_micros / 1_000_000).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function DialogoDeEdicaoDaOportunidade({
  card,
  salvando,
  erro,
  corrigindo = false,
  erroDaCorrecao,
  aoCorrigirNota,
  aoSalvar,
  aoFechar,
}: {
  card: CardDoFunil;
  salvando: boolean;
  /** A correção da nota é salva NA HORA, e não junto do formulário. Ver abaixo. */
  corrigindo?: boolean;
  erroDaCorrecao?: string | null;
  aoCorrigirNota?: (notaId: string, texto: string) => void;
  /*
   * A recusa aparece AQUI, e não numa faixa da tela.
   *
   * O diálogo fica aberto com o que a pessoa digitou: um 409 de "alguém editou
   * antes" pede reler e decidir, e fechar o formulário jogaria fora o texto
   * junto com a explicação. É também por isso que a mensagem do servidor vem
   * inteira — ela diz o que fazer.
   */
  erro?: string | null;
  /** Recebe SÓ os campos alterados. Vazio nunca chega: o botão fica travado. */
  aoSalvar: (mudancas: EdicaoDaOportunidade) => void;
  aoFechar: () => void;
}): ReactNode {
  const [nome, definirNome] = useState(card.titulo ?? '');
  const [valor, definirValor] = useState(emReais(card.valor));
  const [dia, definirDia] = useState(emDiaLocal(card.fechamento_previsto_em));
  const [cliente, definirCliente] = useState<EscolhaDeCliente | null>(
    card.cliente?.id ? { id: card.cliente.id, nome_exibicao: card.cliente.nome_exibicao } : null,
  );

  const micros = emMicros(valor);
  const valorInvalido = valor.trim() !== '' && micros === null;
  // Os campos personalizados (#568). O obrigatório da etapa atual quem confere é
  // o servidor: a recusa volta aqui, na faixa de erro, dizendo qual falta.
  const camposDaOportunidade = useCamposPersonalizados('oportunidade');
  const [mudancaDosCampos, definirMudancaDosCampos] = useState<MudancaDeCampos>({});

  const mudancas: EdicaoDaOportunidade = {
    ...(nome.trim() !== (card.titulo ?? '') && nome.trim() !== '' ? { titulo: nome.trim() } : {}),
    ...(emReais(card.valor) !== valor
      ? { valor: micros === null ? null : { quantia_micros: micros, moeda: 'BRL' } }
      : {}),
    ...(emDiaLocal(card.fechamento_previsto_em) !== dia
      ? { fechamento_previsto_em: emInstanteDoDia(dia) }
      : {}),
    ...((cliente?.id ?? null) !== (card.cliente?.id ?? null)
      ? { cliente_id: cliente?.id ?? null }
      : {}),
    ...(Object.keys(mudancaDosCampos).length > 0 ? { campos: mudancaDosCampos } : {}),
  };

  const mexeuEmAlgo = Object.keys(mudancas).length > 0;
  const podeSalvar = mexeuEmAlgo && !valorInvalido && nome.trim() !== '' && !salvando;

  return (
    <Dialogo
      titulo="Editar oportunidade"
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao
            onClick={aoFechar}
            desabilitado={salvando}
            motivo="Aguarde: a edição está sendo salva."
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="editar-oportunidade"
            variante="primario"
            desabilitado={!podeSalvar}
            /*
              O motivo diz QUAL das três travas está ativa. "Salvar
              desabilitado" sem dizer por quê é o que faz a pessoa clicar três
              vezes e desistir.
            */
            motivo={
              salvando
                ? 'Salvando…'
                : nome.trim() === ''
                  ? 'O nome não pode ficar vazio.'
                  : valorInvalido
                    ? 'Corrija o valor para salvar.'
                    : 'Mude algo para salvar.'
            }
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Botao>
        </>
      }
    >
      {erro != null && (
        <p className="dialogo__erro" role="alert">
          {erro}
        </p>
      )}

      <form
        id="editar-oportunidade"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!podeSalvar) return;
          aoSalvar(mudancas);
        }}
      >
        <Campo
          rotulo="Nome"
          type="text"
          value={nome}
          autoFocus
          maxLength={500}
          onChange={(evento) => definirNome(evento.target.value)}
        />

        <Campo
          rotulo="Valor"
          type="text"
          inputMode="decimal"
          value={valor}
          placeholder="R$ 0,00"
          ajuda="Apague para deixar a oportunidade sem valor."
          aria-invalid={valorInvalido}
          onChange={(evento) => definirValor(evento.target.value)}
        />
        {valorInvalido && (
          <p className="dialogo__erro" role="alert">
            Não entendi esse valor. Escreva como você fala: 12.500,00 ou 12500.
          </p>
        )}

        <Campo
          rotulo="Previsão de fechamento"
          type="date"
          value={dia}
          ajuda="Apague para deixar sem previsão."
          onChange={(evento) => definirDia(evento.target.value)}
        />

        <EscolherCliente escolhido={cliente} aoEscolher={definirCliente} />

        <CamposEditaveis
          id="editar-oportunidade-campo"
          campos={camposDaOportunidade.data?.itens ?? []}
          valores={card.campos}
          mudanca={mudancaDosCampos}
          aoMudar={definirMudancaDosCampos}
        />
      </form>

      {/*
        Aqui as notas aparecem para CORRIGIR, não para escrever — escrever é na
        ficha da oportunidade, que é onde se acompanha a negociação.

        A correção é salva sozinha, e fora do `form`: amarrá-la ao Salvar
        perderia o texto quando o salvamento falhasse por conflito de versão,
        que é justamente o caso em que a pessoa vai reler tudo e tentar de novo.

        O bloco só existe com `card.id`: sem o canônico não há nota nenhuma
        endereçável, e o card recém-vindo do CRM ainda não o tem.
      */}
      {card.id && aoCorrigirNota && (
        <NotasParaCorrigir
          oportunidadeId={card.id}
          aoCorrigir={aoCorrigirNota}
          corrigindo={corrigindo}
          erro={erroDaCorrecao}
        />
      )}
    </Dialogo>
  );
}
